import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Label,
  Input,
  FormGroup,
  Table,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { APIgetRoles } from '../../../api/Roles';

import LoadingSmall from '../../LoadingSmall';
import HelpIcon from '../../HelpIcon';

const RoleSettings = ({
  entity,
  entityType,
  permissions,
  handleChangeRole,
}) => {
  const intl = useIntl();

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);

  useEffect((() => {
    setRolesLoading(true);
    APIgetRoles({
      all: true,
      include_builtin_roles: true,
      include_capabilities: true,
      taxonomy_roles: true,
    }).then((response) => {
      if (response.data) {
        setRoles(response.data);

        if (entityType === 'taxonomy') {
          response.data.forEach((role) => {
            const isAssigned = entity.roles.some(r => r.id === role.id);
            if (!isAssigned) {
              handleChangeRole(role.id, 'can_view', true);
            }
          });
        }

        if (entityType === 'custom-field') {
          response.data.forEach((role) => {
            const isAssigned = entity.roles.some(r => r.id === role.id);
            if (!isAssigned) {
              handleChangeRole(role.id, 'can_view', true);
            }
          });
        }

        setRolesLoading(false);
      }
    });
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  const onChangeRole = (roleId, checked, permission) => {
    if (permission !== undefined) {
      handleChangeRole(roleId, permission, checked);
    } else {
      handleChangeRole(roleId);
    }
  };

  const isChecked = (role, permission) => {
    const roleInEntity = entity.roles.find(tr => tr.id === role.id);
    return roleInEntity ? roleInEntity[permission] : true;
  };

  const isAllChecked = (permissionKey) => (
    roles.length > 0 && roles.every(role => isChecked(role, permissionKey))
  );

  const isSomeChecked = (permissionKey) => (
    roles.some(role => isChecked(role, permissionKey))
  );

  const onChangeAll = (permissionKey, checked) => {
    roles.forEach((role) => {
      if (isChecked(role, permissionKey) !== checked) {
        onChangeRole(role.id, checked, permissionKey);
      }
    });
  };

  const isIndeterminate = (permissionKey) => (
    !isAllChecked(permissionKey) && isSomeChecked(permissionKey)
  );

  const switchGroupClassName = (roleId, permission) => (`switchGroup-${roleId}-${permission}`);

  const setIndeterminate = (permissionKey) => (el) => {
    if (el) {
      el.indeterminate = isIndeterminate(permissionKey); // eslint-disable-line no-param-reassign
    }
  };

  const getTooltip = (role, permission, isCheckedValue) => {
    if (!role || !role.capabilities || !Array.isArray(role.capabilities)) {
      return false;
    }

    if (entityType === 'taxonomy') {
      if (permission === 'can_add' &&
        !role.capabilities.includes('upload_file') &&
        isCheckedValue
      ) {
        return intl.formatMessage({ defaultMessage: 'Check Custom Role settings - uploading or editing must be enabled' });
      }
    }

    return false;
  };

  return (
    <FormGroup>
      {rolesLoading ? (
        <LoadingSmall />
      ) : (
        <div className="table-wrapper">
          <Table className="role-settings-table" responsive>
            <thead>
              <tr>
                <th className="role-settings-role-name">{intl.formatMessage({ defaultMessage: 'Role' })}</th>
                {permissions.map(permission => (
                  <th key={permission.key} className="text-nowrap">
                    <Label for={`${permission.key}-master-checkbox`} className="master-checkbox-label" check>
                      <Input
                        type="checkbox"
                        className="input-check check-s"
                        id={`${permission.key}-master-checkbox`}
                        checked={isAllChecked(permission.key)}
                        innerRef={setIndeterminate(permission.key)}
                        onChange={e => onChangeAll(permission.key, e.target.checked)}
                      />
                      {' '}
                      {permission.label}
                    </Label>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {roles.map(role => (
                <tr key={role.id}>
                  <th className="role-name">
                    {role.name}
                  </th>
                  {permissions.map(permission => {
                    const isCheckedValue = isChecked(role, permission.key);
                    const tooltip = getTooltip(role, permission.key, isCheckedValue);

                    return (
                      <td key={`${role.id}-${permission.key}-checkbox`}>
                        <Input
                          type="checkbox"
                          className="input-check check-s"
                          name={permission.key !== undefined ? `entityRole[${permission.key}]` : 'entityRole'}
                          id={switchGroupClassName(role.id, permission.key)}
                          checked={isCheckedValue}
                          onChange={(e) => onChangeRole(role.id, e.target.checked, permission.key)}
                        />
                        {tooltip && (
                          <span className="ml-1">
                            <HelpIcon permanent={true}>
                              {tooltip}
                            </HelpIcon>
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </FormGroup>
  );
};

RoleSettings.propTypes = {
  entity: PropTypes.object.isRequired,
  entityType: PropTypes.string.isRequired,
  permissions: PropTypes.array.isRequired,
  handleChangeRole: PropTypes.func.isRequired,
};

export default RoleSettings;
